import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Permissao, UsuarioSessao } from '@wms/shared';
import { chatCarregado, limparChatManifest } from '@/lib/chatManifest';
import { api, ErroHttp } from '@/lib/api';

/** Prefixo da troca temporária de crachá (`useCracha`); o id do usuário vem depois. */
export const PREFIXO_CRACHA = 'wms.cracha.';

interface ValorSessao {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  erro: unknown;
  temPermissao: (permissao: Permissao) => boolean;
  entrar: (login: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  entrando: boolean;
  erroLogin: unknown;
  /** Esquece o erro do login (ex.: sair da troca de senha resetada). */
  limparErroLogin: () => void;
  /** Troca a senha resetada (Mudar123) e já entra. */
  trocarSenha: (dados: DadosTrocaSenha) => Promise<void>;
  trocandoSenha: boolean;
  erroTrocaSenha: unknown;
}

export interface DadosTrocaSenha {
  login: string;
  senhaAtual: string;
  senhaNova: string;
  confirmacao: string;
}

/*
  O LOGIN SÓ VALE SE O NAVEGADOR GUARDOU O COOKIE.

  A resposta do login traz o usuário, e confiar nela punha a pessoa
  "logada" numa tela em que toda chamada seguinte voltava 401: sem
  centro, sem nada, e sem dizer por quê. Uma volta em `/api/auth/eu`
  prova que a sessão chegou ao navegador; se não chegou, a frase diz
  onde olhar.
*/
async function conferirCookie(): Promise<void> {
  try {
    await api.get<UsuarioSessao>('/api/auth/eu');
  } catch (erro) {
    if (erro instanceof ErroHttp && erro.naoAutenticado) {
      throw new ErroHttp(401, {
        erro: 'cookie_recusado',
        mensagem:
          'A senha está certa, mas este navegador não guardou o login. ' +
          'Confira a data e a hora do computador e se os cookies estão liberados para este site.',
      });
    }
    throw erro;
  }
}

const ContextoSessao = React.createContext<ValorSessao | null>(null);

export function ProvedorSessao({ children }: { children: React.ReactNode }): JSX.Element {
  const cliente = useQueryClient();

  const consulta = useQuery<UsuarioSessao>({
    queryKey: ['sessao'],
    queryFn: () => api.get<UsuarioSessao>('/api/auth/eu'),
    // 401 aqui é resposta esperada (ninguém logado), não falha de rede.
    retry: (tentativas, erro) =>
      !(erro instanceof ErroHttp && (erro.naoAutenticado || erro.semPermissao)) && tentativas < 2,
    staleTime: 5 * 60_000,
  });

  const login = useMutation({
    mutationFn: async (dados: { login: string; senha: string }) => {
      const usuario = await api.post<UsuarioSessao>('/api/auth/login', dados);
      await conferirCookie();
      return usuario;
    },
    onSuccess: (usuario) => {
      cliente.setQueryData(['sessao'], usuario);
      void cliente.invalidateQueries({ queryKey: ['contexto'] });
    },
  });

  const troca = useMutation({
    mutationFn: async (dados: DadosTrocaSenha) => {
      const usuario = await api.post<UsuarioSessao>('/api/auth/trocar-senha', dados);
      await conferirCookie();
      return usuario;
    },
    onSuccess: (usuario) => {
      login.reset();
      cliente.setQueryData(['sessao'], usuario);
      void cliente.invalidateQueries({ queryKey: ['contexto'] });
    },
  });

  const logout = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>('/api/auth/logout'),
    onSettled: () => {
      cliente.clear();
      // O chat da Manifest guarda a sessão no navegador: quem entrar depois
      // não pode herdar a conversa nem a identidade de quem saiu.
      const recarregar = chatCarregado();
      limparChatManifest();
      if (recarregar) window.setTimeout(() => window.location.replace('/entrar'), 0);
      // A troca de crachá é desta sessão: quem entrar depois começa do cadastro.
      try {
        for (const chave of Object.keys(sessionStorage)) {
          if (chave.startsWith(PREFIXO_CRACHA)) sessionStorage.removeItem(chave);
        }
      } catch {
        /* storage bloqueado: nada foi guardado */
      }
    },
  });

  const usuario = consulta.data ?? null;

  const valor = React.useMemo<ValorSessao>(
    () => ({
      usuario,
      carregando: consulta.isLoading,
      erro: consulta.error,
      temPermissao: (permissao) =>
        !!usuario && (usuario.adminMaster || usuario.permissoes.includes(permissao)),
      entrar: async (login_, senha) => {
        await login.mutateAsync({ login: login_, senha });
      },
      sair: async () => {
        await logout.mutateAsync();
      },
      entrando: login.isPending,
      erroLogin: login.error,
      limparErroLogin: () => {
        login.reset();
        troca.reset();
      },
      trocarSenha: async (dados) => {
        await troca.mutateAsync(dados);
      },
      trocandoSenha: troca.isPending,
      erroTrocaSenha: troca.error,
    }),
    [usuario, consulta.isLoading, consulta.error, login, troca, logout],
  );

  return <ContextoSessao.Provider value={valor}>{children}</ContextoSessao.Provider>;
}

export function useSessao(): ValorSessao {
  const valor = React.useContext(ContextoSessao);
  if (!valor) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>.');
  return valor;
}
